import { HERO_DECK_BREAKPOINT, selectHeroDeckMarkets } from './hero-deck';
import { MarketCard, MarketCardSkeleton } from './market-card';
import { useMediaQuery } from '@/client/hooks/use-media-query';
import { useTrendingMarkets } from '@/client/hooks/use-trending-markets';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Market-Forward hero (2026-08-04 design handoff). Replaces the seal-panel
// diptych: positioning copy (left) + a staggered deck of live markets (right),
// so the first screen sells the product with the product. The deck reuses
// MarketCard on the volume-sorted trending query (shared react-query cache
// with TrendingNow's fallback branch — no extra request). Stats band and
// regulatory claim from the design draft were dropped deliberately: no real
// aggregate numbers to back the former, no license to back the latter.
//
// Mobile (≤ HERO_DECK_BREAKPOINT): the deck is not rendered at all. Stacked
// under the copy it duplicated the Trending rail card-for-card (QA ISSUE-001);
// TrendingNow keeps the top slots there instead. Not CSS display:none — the
// rail's dedupe must know whether the deck is actually shown.
export const TheHero = memo(() => {
  const { t } = useTranslation(['home', 'translation']);
  const setHowItWorksOpen = useModelStore(state => state.setHowItWorksOpen);
  const isMobile = useMediaQuery(`(max-width: ${HERO_DECK_BREAKPOINT}px)`);

  const { data, isLoading } = useTrendingMarkets();
  const deck = useMemo(() => selectHeroDeckMarkets(data?.pages.flatMap(p => p.markets) ?? []), [data]);
  const showDeck = !isMobile && (isLoading || deck.length > 0);

  const scrollToMarkets = () => {
    document.querySelector('#market-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header className={cn('saker-hero', !showDeck && 'saker-hero-solo')}>
      <div className="saker-hero-copy">
        <p className="saker-eyebrow saker-rise d1">{t('hero-eyebrow', 'United Arab Emirates')}</p>
        <h1 className="saker-h1 saker-rise d2">
          {t('hero-title-lead', "The UAE's first ")}
          <span className="accent">{t('hero-title-accent', 'regulated')}</span>
          {t('hero-title-tail', ' prediction market')}
        </h1>
        <p className="saker-sub saker-rise d3">
          {t(
            'hero-sub',
            'Trade real-world outcomes on a compliance-first exchange — every position settled on-chain in USDT.'
          )}
        </p>
        <div className="saker-ctas saker-rise d4">
          <button type="button" className="saker-cta-primary" onClick={scrollToMarkets}>
            {t('hero-start', 'Start trading')}
          </button>
          {/* Distinct intent from the primary CTA: the primary goes to markets,
              this explains the venue (taste audit F2 — two market-bound CTAs
              read as one duplicated intent, and the label collided with the
              "Browse markets" section title below). */}
          <button type="button" className="saker-cta-ghost" onClick={() => setHowItWorksOpen(true)}>
            {t('how-it-works', { ns: 'translation', defaultValue: 'How it works' })}
          </button>
        </div>
        <ul className="saker-creds saker-rise d5">
          <li>{t('hero-cred-1', 'Settled on-chain in USDT')}</li>
          <li>{t('hero-cred-2', 'Oracle-resolved')}</li>
          <li>{t('hero-cred-3', 'Non-custodial')}</li>
        </ul>
      </div>

      {showDeck && (
        <div className="saker-deck saker-rise d3">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={cn('saker-deck-item', i === 0 && 'side', i === 2 && 'side r')}>
                <MarketCardSkeleton />
              </div>
            ))}
          {!isLoading &&
            deck.map((market, i) => (
              <div key={market.marketAddress} className={cn('saker-deck-item', i === 0 && 'side', i === 2 && 'side r')}>
                <MarketCard market={market} index={i} />
              </div>
            ))}
        </div>
      )}
    </header>
  );
});

TheHero.displayName = 'TheHero';
